import { DOCUMENT } from '@angular/common';
import {
    ChangeDetectorRef,
    Directive,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { RequestService } from '@df/api/request/request.service';
import { ProductListing } from '@df/catalog-ui/listing/product-listing';
import { ProductListingFactory } from '@df/catalog-ui/listing/product-listing.factory';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { Status } from '@df/core/status';
import { TimeoutService } from '@df/ng/timeout.service';
import { SearchResult } from '@df/search/search-result';
import { SearchService } from '@df/search/search.service';
import { Subscription } from 'rxjs';
import { NSearch } from './df-search.config';
import { SEARCH_PATH } from './search.resolver';

export class SearchChangeEvent<T = any> {
    constructor(
        /** Reference to the select that emitted the change event. */
        public source: SearchComponentDef,
        /** Current value of the select that emitted the event. */
        public value: T
    ) {}
}

@Directive()
export abstract class SearchComponentDef implements OnInit, OnDestroy {
    /**
     * Search id for events
     */
    @Input({
        required: true
    })
    searchId!: string;

    /**
     * Emit focus change event
     */
    @Output()
    focusChange = new EventEmitter<SearchChangeEvent<boolean>>();

    /**
     * Input element
     * important: you need to put #input on input element in template
     */
    @ViewChild('input')
    input?: ElementRef;

    productListing?: ProductListing;

    protected subscriptions: Subscription = new Subscription();

    protected listingSubscription?: Subscription;

    /**
     * Status model
     */
    status = new Status();

    /**
     * If focused on search
     */
    private _isFocus = false;

    /**
     * term setter
     */
    set isFocus(value: boolean) {
        // always refocus on input if isFocus change
        if (this.input) {
            if (value) {
                this.handleFocus();
            } else {
                this.handleBlur();
            }
        }

        if (this._isFocus !== value) {
            this.onFocusChange(value);
        }
    }

    /**
     * term fetter
     */
    get isFocus(): boolean {
        return this._isFocus;
    }

    /**
     * Search results
     */
    protected _result?: SearchResult;
    set result(result: SearchResult | undefined) {
        this.resultSetter(result);
    }

    get result(): SearchResult | undefined {
        return this._result;
    }

    /**
     * Search term
     */
    private _term = '';

    /**
     * term setter
     */
    set term(term: string) {
        if (this._term !== term) {
            this._term = term;

            this.onTermChange();
        }
    }

    /**
     * term fetter
     */
    get term(): string {
        return this._term;
    }

    protected readonly validTermLength: number = 3;

    /**
     * Meta service subscription
     */
    private metaSubscription: Subscription | undefined;

    onTermChange: () => void;

    /**
     * Class constructor
     */
    constructor(
        readonly app: App,
        @Inject(DOCUMENT) protected document: Document,
        protected router: Router,
        protected searchService: SearchService,
        protected renderer2: Renderer2,
        protected requestService: RequestService,
        protected meta: Meta,
        protected timeoutService: TimeoutService,
        protected productListingFactory: ProductListingFactory,
        protected _ref: ChangeDetectorRef,
        @Inject(NSearch.OPTIONS) protected config: NSearch.IModuleOptions
    ) {
        this.onTermChange = this.timeoutService.throttle(() => this._onTermChange(), 1000, { leading: false, trailing: true });
    }

    /**
     * On init hook
     */
    ngOnInit(): void {
        if (this.config.blurOnLocation) {
            this.metaSubscription = this.meta.subscribe(() => (this.isFocus = false));
        }
    }

    /**
     * On destroy hook
     */
    ngOnDestroy(): void {
        if (this.metaSubscription) {
            this.metaSubscription.unsubscribe();
        }
        this.subscriptions.unsubscribe();
        this.unsubscribeFromProductListing();
    }

    /**
     * Request results if term is valid
     */
    search(): void {
        // search only when searchId is passed - without it you cant pass to outlet, and this lets us control work by attr
        if (this.isValid() && this.searchId) {
            this.requestService.send(this.status, () =>
                this.searchService.search({ q: this.term, ...this.config.options.search }).then(result => this.onSearchResult(result))
            );
        }
    }

    /**
     * Reset to initial state
     */
    reset(): void {
        this.term = '';
        this.result = undefined;
        this.unsubscribeFromProductListing();
        this.productListing = undefined;
        this.searchService.clear(this.searchId);
        this.isFocus = false;
    }

    /**
     * Redirects to search page
     */
    goToSearch(): void {
        if (this.isValid()) {
            this.router.navigate([SEARCH_PATH, this.term]);

            this.reset();
        }
    }

    protected onFocusChange(value: boolean): void {
        this._isFocus = value;

        // body class
        this.setBodyClass(value);

        this.focusChange.emit(new SearchChangeEvent<boolean>(this, value));

        this._ref.detectChanges();
    }

    protected setBodyClass(value: boolean): void {
        value
            ? this.renderer2.addClass(this.document.body, 'search-is-focus')
            : this.renderer2.removeClass(this.document.body, 'search-is-focus');
    }

    protected resultSetter(result: SearchResult | undefined): void {
        this._result = result;
    }

    /**
     * Method called whenever term changes
     */
    protected _onTermChange(): void {
        if (this.config.embeddedMinisearch) {
            this.goToSearch();
        } else {
            this.search();
        }
    }

    protected onSearchResult(result: SearchResult | undefined): void {
        if (this.isFocus) {
            // bind result
            this.result = result;

            // update listing
            this.listingUpdate(result);

            // notify
            this.notify(result);

            // detect changes
            this.detectChanges();
        }
    }

    protected listingUpdate(result: SearchResult | undefined) {
        if (result?.products?.length) {
            if (this.productListing) {
                this.productListing.setSourceModels(result.products);
                this.productListing.apply();
            } else {
                this.unsubscribeFromProductListing();

                this.productListing = this.productListingFactory.create<Product<IProductDataV2>>(
                    {
                        type: 'product',
                        source: result,
                        storage: false,
                        custom: {
                            pioAllowed: false
                        }
                    },
                    result.products?.map(i => i.id) || []
                );

                this.listingSubscription = this.productListing.subscribe(() => this.detectChanges());
            }
        } else {
            this.unsubscribeFromProductListing();

            this.productListing = undefined;
        }
    }

    protected notify(result: SearchResult | undefined): void {
        this.searchService.next(this.searchId, result);
    }

    /**
     * Is searched term is valid.
     */
    protected isValid() {
        return this.term && this.term.length >= this.validTermLength;
    }

    protected unsubscribeFromProductListing(): void {
        if (this.listingSubscription) {
            this.listingSubscription.unsubscribe();
            this.listingSubscription = undefined;
        }
        if (this.productListing) {
            this.productListing.clearSubscriptions();
        }
    }

    protected handleFocus(): void {
        this.timeoutService.setTimeout(() => this.input?.nativeElement.focus(), 0);
    }

    protected handleBlur(): void {
        this.timeoutService.setTimeout(() => this.input?.nativeElement.blur(), 0);
    }

    protected detectChanges(): void {
        this._ref.detectChanges();
    }
}
